import { defaultCtx } from "~/lib/AjaxCtx"
import { ucfirst, ellipsis } from "~/lib/utils"

export default class SocialService {
    constructor(ctx) {
        this.$http = ctx || defaultCtx
    }

    /**
     * It returns an array with all comments for this resource
     */
    async getComments(resourceId) {
        if (!resourceId) {
            throw new Error(`There is no resourceId`)
        }
        return await this.$http.fetchData(`/socials/${resourceId}/comments`)
    }

    /**
     * It returns the number of comments after adding this comment
     */
    async addComment({ resourceId, comment }) {
        if (!resourceId) {
            throw new Error(`There is no resourceId`)
        }
        return await this.$http.fetchData(`/socials/${resourceId}/comment`, {
            method: "POST",
            body: JSON.stringify({ comment }),
        })
    }

    /**
     * It returns the updated resource
     */
    async likeResource(resource) {
        const resourceId = resource.resourceId
        if (!resourceId) {
            throw new Error(
                `The element ${resource._meta.type} has no resourceId`
            )
        }
        const liked = resource.social.liked
        const { social } = await this.$http.fetchData(
            `/socials/${resourceId}/like`,
            {
                method: "POST",
                body: JSON.stringify({ liked }),
            }
        )
        const newSocial = Object.assign({}, resource.social, social)
        return Object.assign({}, resource, {
            social: newSocial,
        })
    }

    async like({ resource, emit }) {
        const likes = resource.social.likes
        const originalResource = Object.assign({}, resource)
        const social = Object.assign({}, resource.social, {
            liked: !resource.social.liked,
            likes: resource.social.liked ? likes - 1 : likes + 1,
        })
        const optimisticResource = Object.assign({}, resource, {
            social,
        })
        emit("update", optimisticResource)

        try {
            const newResource = await this.likeResource(optimisticResource)
            emit("update", newResource)
        } catch (error) {
            emit("update", originalResource)
            throw error
        }
    }

    /**
     * It returns the updated resource
     */
    async bookmarkResource(resource) {
        const resourceId = resource.resourceId
        if (!resourceId) {
            throw new Error(
                `The element ${resource._meta.type} has no resourceId`
            )
        }
        const bookmarked = resource.social.bookmarked
        const { social } = await this.$http.fetchData(
            `/socials/${resourceId}/bookmark`,
            {
                method: "POST",
                body: JSON.stringify({ bookmarked }),
            }
        )
        const newSocial = Object.assign({}, resource.social, social)
        return Object.assign({}, resource, {
            social: newSocial,
        })
    }

    async bookmark({ resource, store, t, title, emit, router, route }) {
        title = title || resource.title
        const originalResource = Object.assign({}, resource)
        const social = Object.assign({}, resource.social, {
            bookmarked: !resource.social.bookmarked,
        })
        const optimisticResource = Object.assign({}, resource, {
            social,
        })
        emit("update", optimisticResource)

        try {
            const newResource = await this.bookmarkResource(optimisticResource)
            const info = newResource.social.bookmarked
                ? t("common.wasAddedToBookmarks")
                : t("common.wasRemovedFromBookmarks")

            const actionText =
                route.name == "bookmarks"
                    ? ""
                    : ucfirst(t("common.myBookmarks"))
            const actionCallback = () => {
                router.push("/bookmarks")
            }

            store.dispatch("pushNotification", {
                title: ellipsis(title, 75),
                info: ucfirst(info),
                type: newResource.social.bookmarked ? "success" : "info",
                actionText,
                actionCallback,
            })
            emit("update", newResource)
        } catch (error) {
            emit("update", originalResource)
            store.dispatch(
                "pushNotification",
                {
                    title: error.message,
                    type: "error",
                },
                { root: true }
            )
            throw error
        }
    }

    /**
     * It returns the updated resource
     */
    async rateResource({ resource, rating }) {
        const resourceId = resource.resourceId
        if (!resourceId) {
            throw new Error(
                `The element ${resource._meta.type} has no resourceId`
            )
        }
        const { social } = await this.$http.fetchData(
            `/socials/${resourceId}/rate`,
            {
                method: "POST",
                body: JSON.stringify({ rating }),
            }
        )
        const newSocial = Object.assign({}, resource.social, social)
        return Object.assign({}, resource, {
            social: newSocial,
        })
    }

    async updateVisitsCount(resource) {
        const { resourceId } = resource
        if (!resourceId) {
            throw new Error(
                `Element of type ${resource._meta.type} has no resourceId`
            )
        }
        const { social } = await this.$http.fetchData(
            `/resources/updateVisitsCount/${resourceId}`
        )
        const newSocial = Object.assign({}, resource.social, social)
        return Object.assign({}, resource, {
            social: newSocial,
        })
    }
}
